import { Text } from '@airwallex/typography';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, DATE_PRECISION, DATE_STYLE } from '@/utils/formatDate';
import { HStack, VStack } from '@airwallex/stacks';
import { Grid } from '@airwallex/grid';
import { StoreContext } from '@/providers/storeProvider';
import { useTabletOrMobile } from '@/hooks/useMediaQuery';
import { labelStyle } from './style';
import i18n from '@/utils/i18n';
import { CreditNoteDetail } from '@/components/CreditNote/types';
import { IconInformational } from '@airwallex/glyphs';
import { useTheme } from '@airwallex/theme';
import { formatCurrency } from '@airwallex/global';
import { formatCardLast4, formatTransaction } from '@/utils/formatPayment';
import BillingFields from '@/components/shared/BillingFields';

const CreditNoteInfo: React.FC<{ creditNote: CreditNoteDetail }> = ({ creditNote }) => {
  const { t } = useTranslation();
  const { invoiceData } = useContext(StoreContext);
  const { legalEntity, customerSnapshot } = invoiceData;
  const { finalizedAt, reason, customReason, refundTransaction } = creditNote;
  const isTabletOrMobile = useTabletOrMobile();
  const { semanticColors } = useTheme();

  const ReasonForCreditOptions = [
    {
      label: t('creditNote.reasonForCredit.productReturn'),
      value: 'PRODUCT_RETURN',
    },
    {
      label: t('creditNote.reasonForCredit.orderChange'),
      value: 'ORDER_CHANGE',
    },
    {
      label: t('creditNote.reasonForCredit.productServiceUnsatisfactory'),
      value: 'PRODUCT_OR_SERVICE_UNSATISFACTORY',
    },
    {
      label: t('creditNote.reasonForCredit.otherBillingErrors'),
      value: 'BILLING_ERROR',
    },
    {
      label: t('creditNote.reasonForCredit.goodwillGesture'),
      value: 'GOODWILL_GESTURE',
    },
    {
      label: t('creditNote.reasonForCredit.other'),
      value: 'OTHER',
    },
  ];

  return (
    <>
      {refundTransaction && (
        <HStack
          align="LEFT"
          space="8px"
          style={{
            padding: '8px 8px 8px 12px',
            borderRadius: '6px',
            border: `1px solid ${semanticColors.border.decorative}`,
            background: semanticColors.background.field,
          }}
        >
          <IconInformational color={semanticColors.icon.information} style={{ flexShrink: 0 }} />
          <VStack>
            <Text variant="hint" color="primary">
              {t('creditNote.refundTipTitle')}
            </Text>
            <Text variant="hint" color="secondary">
              {t('creditNote.refundTipDesc', {
                amount: formatCurrency({
                  amount: refundTransaction.amount,
                  currency: refundTransaction.currency,
                  showSymbol: true,
                  showCode: false,
                }),
                paymentMethod: `${formatTransaction(refundTransaction)} ${formatCardLast4(
                  refundTransaction.cardLast4
                )}`,
                refundDate: formatDate(refundTransaction.succeededAt, i18n.language),
              })}
            </Text>
          </VStack>
        </HStack>
      )}
      {!isTabletOrMobile ? (
        <Grid data-test="credit-note-info" columns="minmax(180px, 1fr) minmax(180px, 1fr)" gap="12px 12px">
          <VStack>
            <Text variant="body" color="secondary">
              {t('creditNote.from')}
            </Text>
            <BillingFields type="seller" data={legalEntity} />
          </VStack>
          <VStack>
            <Text variant="body" color="secondary">
              {t('creditNote.to')}
            </Text>
            <BillingFields type="customer" data={customerSnapshot} />
          </VStack>
          <VStack>
            <Text variant="body" color="secondary">
              {t('creditNote.date')}
            </Text>
            <Text variant="body" style={labelStyle}>
              {formatDate(finalizedAt, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY)}
            </Text>
          </VStack>
          <VStack>
            <Text variant="body" color="secondary">
              {t('creditNote.reason')}
            </Text>
            <Text as="div" variant="body" style={labelStyle}>
              {reason === 'OTHER'
                ? customReason
                : ReasonForCreditOptions.find((option) => option.value === reason)?.label}
            </Text>
          </VStack>
        </Grid>
      ) : (
        <Grid data-test="credit-note-info" columns="120px 1fr" gap="4px 32px">
          <Text variant="body" color="secondary">
            {t('creditNote.from')}
          </Text>
          <VStack style={{ overflow: 'hidden' }}>
            <BillingFields type="seller" data={legalEntity} />
          </VStack>
          <Text variant="body" color="secondary">
            {t('creditNote.to')}
          </Text>
          <VStack style={{ overflow: 'hidden' }}>
            <BillingFields type="customer" data={customerSnapshot} />
          </VStack>
          <Text variant="body" color="secondary">
            {t('creditNote.date')}
          </Text>
          <Text variant="body" style={labelStyle}>
            {formatDate(finalizedAt, i18n.language, DATE_STYLE.NATURAL, DATE_PRECISION.DATE_ONLY)}
          </Text>
          <Text variant="body" color="secondary">
            {t('creditNote.reason')}
          </Text>
          <Text as="div" variant="body" style={labelStyle}>
            {reason === 'OTHER'
              ? customReason
              : ReasonForCreditOptions.find((option) => option.value === reason)?.label}
          </Text>
        </Grid>
      )}
    </>
  );
};

export default CreditNoteInfo;
